import { useState, useEffect, useCallback } from "react";
import { supabase } from "../../../lib/supabase";
import { useAuth } from "../../../contexts/AuthContext";
import { useCompany } from "../../../contexts/CompanyContext";

interface WeeklyReport {
  id: string;
  week_label: string;
  week_start: string;
  done_this_week: string;
  difficulties: string;
  next_week_plan: string;
  status: "submitted" | "reviewed" | "returned";
  admin_comment: string | null;
  submitted_at: string;
  reviewed_at: string | null;
}

function getMonday(d: Date): Date {
  const date = new Date(d);
  const day = date.getDay();
  const diff = date.getDate() - day + (day === 0 ? -6 : 1);
  date.setDate(diff);
  date.setHours(0, 0, 0, 0);
  return date;
}

function getWeekLabel(monday: Date): string {
  const year = monday.getFullYear();
  const jan1 = new Date(year, 0, 1);
  const weekNum = Math.ceil(((monday.getTime() - jan1.getTime()) / 86400000 + jan1.getDay() + 1) / 7);
  return `${year}-W${String(weekNum).padStart(2, "0")}`;
}

function formatWeekRange(weekStart: string): string {
  const mon = new Date(weekStart);
  const fri = new Date(mon);
  fri.setDate(mon.getDate() + 4);
  const fmt = (d: Date) => `${d.getMonth() + 1}/${d.getDate()}`;
  return `${mon.getFullYear()}년 ${mon.getMonth() + 1}월 ${fmt(mon)} ~ ${fmt(fri)} (5일)`;
}

const STATUS_CONFIG = {
  submitted: { label: "검토 대기", color: "#7A9BFF", icon: "ri-time-line", bg: "rgba(122,155,255,0.08)" },
  reviewed: { label: "완료", color: "#00C896", icon: "ri-checkbox-circle-fill", bg: "rgba(0,200,150,0.08)" },
  returned: { label: "반려 (수정 필요)", color: "#E8375F", icon: "ri-arrow-go-back-line", bg: "rgba(232,55,95,0.08)" },
};

const FIELDS = [
  { key: "done_this_week" as const, label: "이번 주에 한 일", placeholder: "이번 주에 완료한 업무, 참여한 태스크, 이벤트 대응 결과 등을 구체적으로 작성하세요.\n예) A&R 허브 ar1 태스크 완료 / 위기 이벤트 '계약 분쟁' 보수적 선택 → 자본금 -500만원 / 팀 협업 메일 2건 처리", hint: "숫자와 구체적인 결과를 포함하면 더 좋아요", icon: "ri-checkbox-line" },
  { key: "difficulties" as const, label: "어려웠던 점 & 배운 것", placeholder: "업무 중 어려웠던 상황, 판단하기 어려웠던 이벤트, 부족하다고 느낀 역량을 솔직하게 작성하세요.\n예) 피처링 섭외 계약서 조항 해석이 어려웠음 → 법무팀 협업 프로세스를 이해하게 됨", hint: "솔직한 회고가 성장의 시작이에요", icon: "ri-lightbulb-line" },
  { key: "next_week_plan" as const, label: "다음 주 계획", placeholder: "다음 주에 완료할 태스크, 개선할 점, 도전할 목표를 작성하세요.\n예) ar2 데모곡 선별 태스크 완료 / 이벤트 발생 시 즉각 대응 / 자본금 1,000만원 이상 달성 목표", hint: "측정 가능한 구체적인 목표가 좋아요", icon: "ri-calendar-check-line" },
];

export default function WeeklyReportPanel() {
  const { user } = useAuth();
  const { workspaceId } = useCompany();

  const today = new Date();
  const monday = getMonday(today);
  const thisWeekLabel = getWeekLabel(monday);
  const thisWeekStart = monday.toISOString().split("T")[0];

  const [currentReport, setCurrentReport] = useState<WeeklyReport | null>(null);
  const [history, setHistory] = useState<WeeklyReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ done_this_week: "", difficulties: "", next_week_plan: "" });
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [showHistory, setShowHistory] = useState(false);

  const fetchReports = useCallback(async () => {
    if (!user || !workspaceId) return;
    setLoading(true);
    const { data } = await supabase
      .from("weekly_reports")
      .select("*")
      .eq("workspace_id", workspaceId)
      .eq("user_id", user.id)
      .order("week_start", { ascending: false });

    const rows = (data ?? []) as WeeklyReport[];
    const curr = rows.find(r => r.week_label === thisWeekLabel) ?? null;
    setCurrentReport(curr);
    setHistory(rows.filter(r => r.week_label !== thisWeekLabel));

    if (curr?.status === "returned") {
      setForm({ done_this_week: curr.done_this_week, difficulties: curr.difficulties, next_week_plan: curr.next_week_plan });
      setEditing(true);
    }
    setLoading(false);
  }, [user, workspaceId, thisWeekLabel]);

  useEffect(() => { fetchReports(); }, [fetchReports]);

  const handleSubmit = async () => {
    if (!user || !workspaceId) return;
    if (!form.done_this_week.trim() || !form.difficulties.trim() || !form.next_week_plan.trim()) return;
    setSubmitting(true);

    if (currentReport?.status === "returned") {
      // 수정 재제출
      await supabase.from("weekly_reports").update({
        done_this_week: form.done_this_week,
        difficulties: form.difficulties,
        next_week_plan: form.next_week_plan,
        status: "submitted",
        admin_comment: null,
        submitted_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      }).eq("id", currentReport.id);
    } else {
      // 최초 제출
      await supabase.from("weekly_reports").insert({
        workspace_id: workspaceId,
        user_id: user.id,
        week_label: thisWeekLabel,
        week_start: thisWeekStart,
        done_this_week: form.done_this_week,
        difficulties: form.difficulties,
        next_week_plan: form.next_week_plan,
        status: "submitted",
      });
    }

    setSubmitting(false);
    setEditing(false);
    setForm({ done_this_week: "", difficulties: "", next_week_plan: "" });
    await fetchReports();
  };

  const startNewReport = () => {
    setForm({ done_this_week: "", difficulties: "", next_week_plan: "" });
    setEditing(true);
  };

  const charCount = (v: string) => v.length;
  const isFormValid = form.done_this_week.trim().length >= 20 && form.difficulties.trim().length >= 20 && form.next_week_plan.trim().length >= 20;

  if (loading) {
    return (
      <div className="bg-[#111112] rounded-xl border border-white/[0.05] p-5">
        <div className="h-4 w-32 bg-white/[0.04] rounded animate-pulse mb-3" />
        <div className="h-24 bg-white/[0.02] rounded animate-pulse" />
      </div>
    );
  }

  return (
    <div className="bg-[#111112] rounded-xl border border-white/[0.05] overflow-hidden">
      {/* 헤더 */}
      <div className="px-5 py-4 border-b border-white/[0.05] flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#FF9F43]/10">
            <i className="ri-file-text-line text-[#FF9F43] text-base"></i>
          </div>
          <div>
            <div className="text-[#F2F2F2] text-sm font-semibold">주간 업무 보고서</div>
            <div className="text-[#444444] text-[10px]">{formatWeekRange(thisWeekStart)}</div>
          </div>
        </div>

        {currentReport && !editing && (
          <div
            className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-semibold border"
            style={{
              color: STATUS_CONFIG[currentReport.status].color,
              borderColor: `${STATUS_CONFIG[currentReport.status].color}30`,
              backgroundColor: STATUS_CONFIG[currentReport.status].bg,
            }}
          >
            <i className={`${STATUS_CONFIG[currentReport.status].icon} text-xs`}></i>
            {STATUS_CONFIG[currentReport.status].label}
          </div>
        )}
      </div>

      <div className="px-5 py-4 space-y-4">

        {/* 이번 주 보고서가 없고 작성 중도 아닐 때 */}
        {!currentReport && !editing && (
          <div className="text-center py-6">
            <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-[#FF9F43]/10 mb-3">
              <i className="ri-file-add-line text-[#FF9F43] text-xl"></i>
            </div>
            <p className="text-[#888888] text-sm font-medium mb-1">이번 주 보고서를 아직 제출하지 않았어요</p>
            <p className="text-[#444444] text-xs mb-4">강사가 내용을 확인하고 피드백을 남길 수 있어요</p>
            <button
              type="button"
              onClick={startNewReport}
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#FF9F43] text-white text-sm font-semibold rounded-xl cursor-pointer hover:bg-[#FFB060] transition-colors whitespace-nowrap"
            >
              <i className="ri-pencil-line text-sm"></i>보고서 작성하기
            </button>
          </div>
        )}

        {/* 작성 폼 */}
        {editing && (
          <div className="space-y-4">
            {currentReport?.status === "returned" && currentReport.admin_comment && (
              <div className="flex gap-2.5 px-3 py-2.5 rounded-lg bg-[#E8375F]/[0.06] border border-[#E8375F]/20">
                <i className="ri-arrow-go-back-line text-[#E8375F] text-sm shrink-0 mt-0.5"></i>
                <div>
                  <div className="text-[#E8375F] text-xs font-semibold mb-0.5">강사 반려 코멘트</div>
                  <p className="text-[#888888] text-xs leading-relaxed">{currentReport.admin_comment}</p>
                </div>
              </div>
            )}

            {FIELDS.map(field => (
              <div key={field.key}>
                <label className="flex items-center gap-1.5 text-[#888888] text-xs mb-1.5">
                  <i className={`${field.icon} text-sm text-[#555555]`}></i>
                  <span className="font-medium text-[#AAAAAA]">{field.label}</span>
                  <span className="text-[#E8375F] text-[10px]">*</span>
                </label>
                <textarea
                  value={form[field.key]}
                  onChange={e => {
                    if (e.target.value.length <= 500) setForm(f => ({ ...f, [field.key]: e.target.value }));
                  }}
                  placeholder={field.placeholder}
                  rows={4}
                  className="w-full bg-[#0C0C0E] text-[#CCCCCC] text-xs rounded-lg px-3 py-2.5 border border-white/[0.08] focus:outline-none focus:border-[#FF9F43]/40 placeholder-[#2A2A2A] resize-none leading-relaxed"
                />
                <div className="flex items-center justify-between mt-1">
                  <span className="text-[#2A2A2A] text-[9px]">{field.hint}</span>
                  <span className={`text-[9px] tabular-nums ${charCount(form[field.key]) < 20 ? "text-[#E8375F]/60" : "text-[#3D3D3D]"}`}>
                    {charCount(form[field.key])}/500 {charCount(form[field.key]) < 20 && `(최소 20자)`}
                  </span>
                </div>
              </div>
            ))}

            <div className="flex gap-3">
              {(currentReport?.status !== "returned") && (
                <button
                  type="button"
                  onClick={() => setEditing(false)}
                  className="px-4 py-2.5 rounded-xl border border-white/[0.08] text-[#555555] text-sm hover:text-[#888888] transition-colors cursor-pointer whitespace-nowrap"
                >
                  취소
                </button>
              )}
              <button
                type="button"
                onClick={handleSubmit}
                disabled={!isFormValid || submitting}
                className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl font-semibold text-sm transition-all whitespace-nowrap ${
                  isFormValid && !submitting
                    ? "bg-[#FF9F43] text-white cursor-pointer hover:bg-[#FFB060]"
                    : "bg-white/[0.04] text-[#2A2A2A] cursor-not-allowed"
                }`}
              >
                {submitting
                  ? <><i className="ri-loader-4-line animate-spin text-sm"></i>제출 중...</>
                  : <><i className="ri-send-plane-line text-sm"></i>
                    {currentReport?.status === "returned" ? "수정 후 재제출" : "보고서 제출"}
                  </>
                }
              </button>
            </div>
          </div>
        )}

        {/* 제출된 보고서 보기 */}
        {currentReport && !editing && (
          <div className="space-y-3">
            {/* 어드민 코멘트 (reviewed) */}
            {currentReport.status === "reviewed" && currentReport.admin_comment && (
              <div className="flex gap-2.5 px-3 py-2.5 rounded-lg bg-[#00C896]/[0.06] border border-[#00C896]/20">
                <i className="ri-user-star-line text-[#00C896] text-sm shrink-0 mt-0.5"></i>
                <div>
                  <div className="text-[#00C896] text-xs font-semibold mb-0.5">강사 피드백</div>
                  <p className="text-[#888888] text-xs leading-relaxed">{currentReport.admin_comment}</p>
                  {currentReport.reviewed_at && (
                    <div className="text-[#3D3D3D] text-[9px] mt-1">
                      {new Date(currentReport.reviewed_at).toLocaleDateString("ko-KR", { month: "long", day: "numeric" })} 검토 완료
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* 보고서 내용 */}
            {FIELDS.map(field => (
              <div key={field.key} className="bg-white/[0.01] rounded-lg border border-white/[0.04] px-3 py-2.5">
                <div className="flex items-center gap-1.5 mb-1.5">
                  <i className={`${field.icon} text-xs text-[#555555]`}></i>
                  <span className="text-[#555555] text-[10px] uppercase tracking-widest">{field.label}</span>
                </div>
                <p className="text-[#AAAAAA] text-xs leading-relaxed whitespace-pre-wrap">{currentReport[field.key]}</p>
              </div>
            ))}

            <div className="flex items-center justify-between text-[10px]">
              <span className="text-[#3D3D3D]">
                제출: {new Date(currentReport.submitted_at).toLocaleDateString("ko-KR", { month: "long", day: "numeric" })}
                {" "}
                {new Date(currentReport.submitted_at).toLocaleTimeString("ko-KR", { hour: "2-digit", minute: "2-digit" })}
              </span>
              {currentReport.status === "submitted" && (
                <span className="text-[#555555]">강사 검토 대기 중...</span>
              )}
            </div>
          </div>
        )}

        {/* 이전 보고서 이력 */}
        {history.length > 0 && (
          <div className="border-t border-white/[0.04] pt-3">
            <button
              type="button"
              onClick={() => setShowHistory(v => !v)}
              className="w-full flex items-center justify-between text-[#444444] hover:text-[#777777] text-xs transition-colors cursor-pointer py-1"
            >
              <span className="flex items-center gap-1.5">
                <i className="ri-history-line text-sm"></i>
                이전 보고서 이력 ({history.length}건)
              </span>
              <i className={`ri-arrow-${showHistory ? "up" : "down"}-s-line text-sm`}></i>
            </button>

            {showHistory && (
              <div className="mt-2 space-y-2">
                {history.map(r => {
                  const cfg = STATUS_CONFIG[r.status];
                  const isOpen = expandedId === r.id;
                  return (
                    <div key={r.id} className="bg-white/[0.01] rounded-lg border border-white/[0.04] overflow-hidden">
                      <button
                        type="button"
                        onClick={() => setExpandedId(isOpen ? null : r.id)}
                        className="w-full flex items-center justify-between px-3 py-2.5 cursor-pointer hover:bg-white/[0.02] transition-colors"
                      >
                        <div className="flex items-center gap-2">
                          <i className={`${cfg.icon} text-xs`} style={{ color: cfg.color }}></i>
                          <span className="text-[#888888] text-xs">{formatWeekRange(r.week_start)}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span
                            className="text-[9px] font-semibold px-1.5 py-0.5 rounded-full border"
                            style={{ color: cfg.color, borderColor: `${cfg.color}30`, backgroundColor: `${cfg.color}10` }}
                          >
                            {cfg.label}
                          </span>
                          <i className={`ri-arrow-${isOpen ? "up" : "down"}-s-line text-[#444444] text-xs`}></i>
                        </div>
                      </button>
                      {isOpen && (
                        <div className="px-3 pb-3 space-y-2 border-t border-white/[0.04] pt-2">
                          {r.admin_comment && (
                            <div className="flex gap-2 px-2.5 py-2 rounded-lg" style={{ backgroundColor: `${cfg.color}08`, borderLeft: `2px solid ${cfg.color}40` }}>
                              <i className="ri-user-star-line text-xs shrink-0 mt-0.5" style={{ color: cfg.color }}></i>
                              <p className="text-[#777777] text-[10px] leading-relaxed">{r.admin_comment}</p>
                            </div>
                          )}
                          {FIELDS.map(f => (
                            <div key={f.key}>
                              <div className="text-[#3D3D3D] text-[9px] uppercase tracking-widest mb-1">{f.label}</div>
                              <p className="text-[#666666] text-[10px] leading-relaxed whitespace-pre-wrap">{r[f.key]}</p>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
